import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  HStack,
  Text,
  Icon,
  Collapse,
  Divider,
  Select,
  Button,
  Badge,
  Checkbox,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Flex,
  Wrap,
  WrapItem,
  Tag,
  SimpleGrid,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { ScenarioAnalysis } from "build-ts/analyst";
import { FiClock, FiSend } from "react-icons/fi";
import { FaListUl } from "react-icons/fa";
import { SearchIcon } from "@chakra-ui/icons";
import { SlackChannel, SlackNotificationConfig, SlackNotificationRequest, SlackUser } from "build-ts/slack_notification";
import { getSlackChannels, getSlackChannelUsers } from "services/AnalystService";
import { Header } from "build-ts/metadata";
import { getOperatorSymbol } from "utils/analysisUtils";
import { getFilterDisplayString } from "utils/nameUtils";
import { ScheduledAnalystProto, ScheduledJobProto, ScheduledJobType } from "build-ts/schedule";
import { MetadataTypeEnum } from "build-ts/enums";
import { v4 as uuidv4 } from "uuid";
import { useParams } from "react-router-dom";
import { putScheduledJob } from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";
import { getScheduledJob } from "services/ScheduleServices";
import CronBuilder from "./CronBuilder";
import lodash from "lodash";

interface JobCreationComponentProps {
  selectedJob?: Header;
  scenarios: ScenarioAnalysis[] | undefined;
  isCreatingNew?: boolean;
  triggerSave?: boolean;
  setTriggerSave?: (value: boolean) => void;
  setIsSaving?: (value: boolean) => void;
  onSaveSuccess?: () => void;
  setHasFormChanges?: (hasChanges: boolean) => void;
}

const JobCreationComponent: React.FC<JobCreationComponentProps> = ({
  selectedJob,
  scenarios,
  isCreatingNew = false,
  triggerSave = false,
  setTriggerSave,
  setIsSaving,
  onSaveSuccess,
  setHasFormChanges,
}) => {
  const { id: analystId } = useParams();

  // Basic Information state
  const [jobName, setJobName] = useState<string>("");
  const [instructions, setInstructions] = useState<string>("");

  // Scenarios state
  const [selectedScenarios, setSelectedScenarios] = useState<ScenarioAnalysis[]>([]);

  // Communication state
  const [slackTitle, setSlackTitle] = useState<string>("");
  const [slackChannels, setSlackChannels] = useState<SlackChannel[]>([]);
  const [selectedSlackChannel, setSelectedSlackChannel] = useState<SlackChannel | null>(null);
  const [channelUsers, setChannelUsers] = useState<SlackUser[]>([]);
  const [selectedSlackUsers, setSelectedSlackUsers] = useState<SlackUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);
  const [lastFetchedChannelId, setLastFetchedChannelId] = useState<string | null>(null);

  // Collapsible sections state
  const [isScheduleExpanded, setIsScheduleExpanded] = useState<boolean>(false);
  const [isScenariosExpanded, setIsScenariosExpanded] = useState<boolean>(false);
  const [isCommunicationExpanded, setIsCommunicationExpanded] = useState<boolean>(false);
  const [selectedJobDetails, setSelectedJobDetails] = useState<ScheduledJobProto | undefined>(undefined);

  //Schedule state
  const [isActive, setIsActive] = useState<boolean>(false);
  const [cronExpression, setCronExpression] = useState<string>("");

  // Search state
  const [scenarioSearchTerm, setScenarioSearchTerm] = useState<string>("");

  // Filter scenarios based on search term
  const filteredScenarios = useMemo(() => {
    if (!scenarios) return [];
    if (!scenarioSearchTerm.trim()) {
      return scenarios;
    }

    const lowerSearchTerm = scenarioSearchTerm.toLowerCase();

    return scenarios.filter((scenario) => {
      // Search in issue description
      const issueMatch = scenario.issue?.toLowerCase().includes(lowerSearchTerm);

      // Search in filter display text
      let filterMatch = false;
      if (scenario.preset_filters && scenario.preset_filters.length > 0) {
        filterMatch = scenario.preset_filters.some((filter) => {
          let displayText = "";
          if (filter.date_filter) {
            displayText = `${filter.kg_column_header?.name}: ${getFilterDisplayString(filter)}`;
          } else if (filter.kg_column_header && filter.values && filter.values.length > 0) {
            const operatorSymbol = getOperatorSymbol(filter.operator!);
            displayText = `${filter.kg_column_header.name} ${operatorSymbol} ${filter.values.join(", ")}`;
          }
          return displayText.toLowerCase().includes(lowerSearchTerm);
        });
      }

      return issueMatch || filterMatch;
    });
  }, [scenarios, scenarioSearchTerm]);

  // Original data state for tracking changes
  const [originalData, setOriginalData] = useState<{
    jobName: string;
    instructions: string;
    selectedScenarios: ScenarioAnalysis[];
    slackTitle: string;
    selectedSlackChannel: SlackChannel | null;
    selectedSlackUsers: SlackUser[];
    cronExpression: string;
  }>({
    jobName: "",
    instructions: "",
    selectedScenarios: [],
    slackTitle: "",
    selectedSlackChannel: null,
    selectedSlackUsers: [],
    cronExpression: "",
  });

  // Get current form data
  const getCurrentFormData = () => {
    return {
      jobName,
      instructions,
      selectedScenarios,
      slackTitle,
      selectedSlackChannel,
      selectedSlackUsers,
      cronExpression
    };
  };

  // Check if form has changes
  useEffect(() => {
    const currentFormData = getCurrentFormData();
    const isChanged = !lodash.isEqual(originalData, currentFormData);
    setHasFormChanges?.(isChanged);
  }, [
    jobName,
    instructions,
    selectedScenarios,
    slackTitle,
    selectedSlackChannel,
    selectedSlackUsers,
    cronExpression,
    isActive,
  ]);

  const fetchJobDetails = async (selectedJobId: string) => {
    const response = await getScheduledJob(selectedJobId, false);
    if (response) {
      setSelectedJobDetails(response);
      const jobNameValue = response.header?.name || "";
      const instructionsValue = response.scheduled_analyst?.instructions || "";
      const foundScenarios = (response.scheduled_analyst?.scenario_ids || [])
        .map((id) => scenarios?.find((s) => s.id === id))
        .filter((s): s is ScenarioAnalysis => s !== undefined);
      const slackTitleValue = response.scheduled_analyst?.slack_notification_request?.notification_config?.message_title || "";
      const slackChannelValue = response.scheduled_analyst?.slack_notification_request?.notification_config?.channel || null;
      const slackUsersValue = response.scheduled_analyst?.slack_notification_request?.notification_config?.tag_users || [];
      const cronExprValue = response.cron_expr || "";
      const isActiveValue = response.is_active || false;

      setJobName(jobNameValue);
      setInstructions(instructionsValue);
      setSelectedScenarios(foundScenarios);
      setSlackTitle(slackTitleValue);
      setSelectedSlackChannel(slackChannelValue);
      setSelectedSlackUsers(slackUsersValue);
      setCronExpression(cronExprValue);
      setIsActive(isActiveValue);

      // Set original data for change tracking
      setOriginalData({
        jobName: jobNameValue,
        instructions: instructionsValue,
        selectedScenarios: foundScenarios,
        slackTitle: slackTitleValue,
        selectedSlackChannel: slackChannelValue,
        selectedSlackUsers: slackUsersValue,
        cronExpression: cronExprValue,
      });
    }
  };


  const handleSaveJob = async () => {
    // Validate required fields
    if (!jobName.trim()) {
      showToast("Please enter a job name", "", "warning");
      return;
    }

    if (selectedScenarios.length === 0) {
      showToast("Please select at least one scenario", "", "warning");
      return;
    }

    setIsSaving?.(true);

    // Create or update job
    const job = ScheduledJobProto.create({
      header: Header.create({
        id: selectedJob?.id || uuidv4(),
        name: jobName.trim(),
        type: MetadataTypeEnum.SCHEDULED_JOB,
        owner_id: analystId,
      }),
      type: ScheduledJobType.ANALYST_JOB,
      cron_expr: cronExpression,
      is_active: isActive,
      scheduled_analyst: ScheduledAnalystProto.create({
        instructions: instructions.trim(),
        scenario_ids: selectedScenarios
          .map((scenario) => scenario.id)
          .filter((id): id is string => id !== undefined),
        slack_notification_request: SlackNotificationRequest.create({
          notification_config: SlackNotificationConfig.create({
            channel: selectedSlackChannel
              ? SlackChannel.create({
                id: selectedSlackChannel.id,
                name: selectedSlackChannel.name,
                is_private: selectedSlackChannel.is_private,
                num_members: selectedSlackChannel.num_members,
              })
              : undefined,
            tag_users: selectedSlackUsers,
            message_title: slackTitle.trim(),
          }),
        }),
      }),
    });

    const response = await putScheduledJob(job);
    setIsSaving?.(false);

    if (response) {
      showToast(
        isCreatingNew ? "Job created successfully" : "Job updated successfully",
        "",
        "success"
      );
      onSaveSuccess?.();
    } else {
      showToast(
        isCreatingNew ? "Failed to create job" : "Failed to update job",
        "",
        "error"
      );
    }
  };

  // Watch for triggerSave changes to trigger save
  useEffect(() => {
    if (triggerSave) {
      handleSaveJob();
      setTriggerSave?.(false);
    }
  }, [triggerSave]);

  // Fetch slack channels on component mount
  useEffect(() => {
    const fetchSlackChannels = async () => {
      const channels = await getSlackChannels();
      if (channels) {
        setSlackChannels(channels);
      }
    };
    fetchSlackChannels();
  }, []);

  // Fetch job details when selectedJob changes
  useEffect(() => {
    if (selectedJob?.id) {
      // Fetch job details - scenarios will be used in fetchJobDetails to map IDs
      fetchJobDetails(selectedJob.id);
    } else {
      // Reset form when no job is selected
      setJobName("");
      setInstructions("");
      setSelectedScenarios([]);
      setSlackTitle("");
      setSelectedSlackChannel(null);
      setSelectedSlackUsers([]);
      setIsLoadingUsers(false);
      setLastFetchedChannelId(null);
      setCronExpression("");
      setIsActive(false);
      setSelectedJobDetails(undefined);

      // Reset original data for change tracking
      setOriginalData({
        jobName: "",
        instructions: "",
        selectedScenarios: [],
        slackTitle: "",
        selectedSlackChannel: null,
        selectedSlackUsers: [],
        cronExpression: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJob?.id]);

  // Fetch slack channel users when channel is selected
  useEffect(() => {
    const fetchChannelUsers = async () => {
      if (selectedSlackChannel?.id) {
        if (selectedSlackChannel.id === lastFetchedChannelId) {
          return;
        }

        setIsLoadingUsers(true);
        try {
          const users = await getSlackChannelUsers(selectedSlackChannel.id);
          if (users) {
            setChannelUsers(users);
          } else {
            setChannelUsers([]);
          }
          setLastFetchedChannelId(selectedSlackChannel.id);
        } catch (error) {
          console.error("Error fetching channel users:", error);
          setChannelUsers([]);
        } finally {
          setIsLoadingUsers(false);
        }
      } else {
        setChannelUsers([]);
        setLastFetchedChannelId(null);
        setSelectedSlackUsers([]);
        setIsLoadingUsers(false);
      }
    };
    fetchChannelUsers();
  }, [selectedSlackChannel]);

  const toggleScheduleExpansion = () => {
    setIsScheduleExpanded(!isScheduleExpanded);
    setIsScenariosExpanded(false);
    setIsCommunicationExpanded(false);
  };

  const toggleScenariosExpansion = () => {
    setIsScenariosExpanded(!isScenariosExpanded);
    setIsScheduleExpanded(false);
    setIsCommunicationExpanded(false);
  };

  const toggleCommunicationExpansion = () => {
    setIsCommunicationExpanded(!isCommunicationExpanded);
    setIsScheduleExpanded(false);
    setIsScenariosExpanded(false);
  };

  // Handle Slack channel selection
  const handleSlackChannelChange = (channelId: string) => {
    if (channelId === "") {
      setSelectedSlackChannel(null);
    } else {
      const channel = slackChannels.find((c) => c.id === channelId);
      setSelectedSlackChannel(channel || null);
    }
    setSelectedSlackUsers([]);
  };

  // Remove selected slack user
  const removeSlackUser = (userId: string) => {
    setSelectedSlackUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  // Handle scenario selection toggle
  const handleScenarioToggle = (scenario: ScenarioAnalysis) => {
    setSelectedScenarios((prev) => {
      const isSelected = prev.some((s) => s.id === scenario.id);
      if (isSelected) {
        return prev.filter((s) => s.id !== scenario.id);
      } else {
        return [...prev, scenario];
      }
    });
  };

  return (
    <Box height="100%" display="flex" flexDirection="column">
      {/* Form Content - Scrollable */}
      <Box
        flex="1"
        overflowY="auto"
        px={4}
        py={1}
        sx={{
          "&::-webkit-scrollbar": {
            display: "none",
          },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
        maxH="85vh"
      >
        {/* Basic Information Section */}
        <VStack spacing={3} align="stretch">
          <FormControl>
            {/* <Collapse in={isBasicInfoExpanded} animateOpacity> */}
            <VStack spacing={3} align="stretch" p={3}>
              <FormControl isRequired>
                <FormLabel fontSize="sm" mb={1} fontWeight="600">
                  Job Name
                </FormLabel>
                <Input
                  placeholder="Enter job name"
                  value={jobName}
                  onChange={(e) => setJobName(e.target.value)}
                  size="sm"
                  _focus={{
                    borderColor: "purple.700",
                    boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)",
                  }}
                  fontWeight="500"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" mb={1} fontWeight="600">
                  Instructions
                </FormLabel>
                <Textarea
                  placeholder="Enter job instructions"
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  rows={6}
                  resize="none"
                  size="sm"
                  _focus={{
                    borderColor: "purple.700",
                    boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)",
                  }}
                  fontWeight="500"
                />
              </FormControl>
            </VStack>
            {/* </Collapse> */}
          </FormControl>
        </VStack>

        <Divider mt={1} mb={1} />

        {/* Scenarios Selection Section */}
        <VStack spacing={3} align="stretch">
          <FormControl>
            <HStack
              spacing={2}
              cursor="pointer"
              onClick={toggleScenariosExpansion}
              _hover={{ bg: "gray.50" }}
              p={2}
              borderRadius="md"
              m={2}
              ml={1}
            >
              <Icon as={FaListUl} boxSize={5} color="fg.default" />
              <FormLabel fontSize="md" mb={0} cursor="pointer" fontWeight="700" color="fg.default">
                Analyses
                {selectedScenarios.length > 0 && (
                  <Badge colorScheme="purple" ml={2}>
                    {selectedScenarios.length}
                  </Badge>
                )}
              </FormLabel>
              {!isScenariosExpanded && (
                <Text fontSize="sm" color="gray.600" mb={0} align="left" ml={1} mt={1}>
                  Select scenarios to include in this job
                </Text>
              )}
              {isScenariosExpanded && (
                <InputGroup
                  size="sm"
                  maxW="500px"
                  onClick={(e) => e.stopPropagation()}
                >
                  <InputLeftElement pointerEvents="none" height="full">
                    <SearchIcon color="gray.400" boxSize={3} />
                  </InputLeftElement>
                  <Input
                    placeholder="Search scenarios by issue or filters..."
                    value={scenarioSearchTerm}
                    onChange={(e) => setScenarioSearchTerm(e.target.value)}
                    bg="gray.50"
                    border="1px"
                    borderColor="gray.200"
                    fontSize="sm"
                    _hover={{
                      borderColor: "gray.300",
                      bg: "white"
                    }}
                    _focus={{
                      borderColor: "purple.400",
                      boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)",
                      bg: "white"
                    }}
                    _placeholder={{
                      color: "gray.400"
                    }}
                  />
                </InputGroup>
              )}
            </HStack>

            <Collapse in={isScenariosExpanded} animateOpacity>
              <VStack spacing={3} align="stretch" p={3} overflowY="auto" maxH="40vh">
                {scenarios && scenarios.length > 0 ? (
                  filteredScenarios.length === 0 ? (
                    <Box p={4} textAlign="center">
                      <Text fontSize="sm" color="gray.500">
                        No scenarios match your search. Try different keywords.
                      </Text>
                    </Box>
                  ) : (
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                      {filteredScenarios.map((scenario) => {
                        const index = scenarios.findIndex(s => s.id === scenario.id);
                        const isSelected = selectedScenarios.some(
                          (s) => s.id === scenario.id
                        );
                        return (
                          <Box
                            key={scenario.id || index}
                            p={3}
                            border="1px"
                            borderColor={isSelected ? "purple.300" : "gray.200"}
                            borderRadius="md"
                            bg={isSelected ? "purple.50" : "white"}
                            cursor="pointer"
                            onClick={() => handleScenarioToggle(scenario)}
                            _hover={{ borderColor: "purple.400" }}
                            transition="all 0.2s"
                          >
                            <HStack spacing={3} align="flex-start">
                              <Checkbox
                                isChecked={isSelected}
                                onChange={() => handleScenarioToggle(scenario)}
                                colorScheme="purple"
                                size="md"
                                mt={0.5}
                              />
                              <VStack align="flex-start" flex={1} spacing={3}>
                                {/* Issue Display */}
                                <HStack spacing={3} w="100%">
                                  <Text fontSize="sm" fontWeight="500" color="gray.800" textAlign="left" mb={0}>
                                    {index + 1} :
                                  </Text>
                                  <Text fontSize="sm" color="gray.700" fontWeight="600" textAlign="left" mb={0}>
                                    {scenario.issue || "No issue description"}
                                  </Text>
                                </HStack>

                                {/* Preset Filters Display */}
                                {scenario.preset_filters && scenario.preset_filters.length > 0 && (
                                  <HStack w="100%" textAlign="left" align="flex-start">
                                    <Wrap
                                      spacing={1}
                                      justify="flex-start"
                                      css={{
                                        // Remove default list spacing from Chakra Wrap (Wrap renders a <ul> tag)
                                        lineHeight: 0,
                                        "& > ul": {
                                          margin: 0,
                                          padding: 0,
                                        }
                                      }}
                                    >
                                      {scenario.preset_filters.map((filter, filterIndex) => {
                                        let displayText = "";
                                        if (filter.date_filter) {
                                          displayText = `${filter.kg_column_header?.name}: ${getFilterDisplayString(filter)}`;
                                        } else if (filter.kg_column_header && filter.values && filter.values.length > 0) {
                                          const operatorSymbol = getOperatorSymbol(filter.operator!);
                                          displayText = `${filter.kg_column_header.name} ${operatorSymbol} ${filter.values.join(", ")}`;
                                        }

                                        return displayText ? (
                                          <WrapItem key={filterIndex}>
                                            <Tag
                                              size="sm"
                                              colorScheme={filter?.is_hidden ? "gray" : "purple"}
                                              fontWeight="500"
                                              flexShrink={0}
                                              bg={filter?.is_hidden ? "gray.50" : "purple.50"}
                                              borderWidth="1px"
                                              borderColor={filter?.is_hidden ? "gray.300" : "purple.300"}
                                              px={2}
                                              py={1}
                                            >
                                              {displayText}
                                            </Tag>
                                          </WrapItem>
                                        ) : null;
                                      })}
                                    </Wrap>
                                  </HStack>
                                )}
                              </VStack>
                            </HStack>
                          </Box>
                        );
                      })}
                    </SimpleGrid>
                  )
                ) : (
                  <Box p={4} textAlign="center">
                    <Text fontSize="sm" color="gray.500">
                      No scenarios available. Create scenarios in the Scenarios tab first.
                    </Text>
                  </Box>
                )}
              </VStack>
            </Collapse>
          </FormControl>
        </VStack>

        <Divider mt={1} mb={1} />

        {/* Communication Section */}
        <VStack spacing={3} align="stretch">
          <FormControl>
            <HStack
              spacing={2}
              cursor="pointer"
              onClick={toggleCommunicationExpansion}
              _hover={{ bg: "gray.50" }}
              p={2}
              borderRadius="md"
              m={2}
              ml={1}
            >
              <Icon as={FiSend} boxSize={5} color="fg.default" />
              <FormLabel fontSize="md" mb={0} cursor="pointer" fontWeight="700" color="fg.default">
                Communication
                {(selectedSlackChannel || selectedSlackUsers.length > 0) && (
                  <Badge colorScheme="purple" ml={2} textTransform="none" fontWeight="700">
                    {selectedSlackChannel ? 1 : 0} channel, {selectedSlackUsers.length} users
                  </Badge>
                )}
              </FormLabel>
              {!isCommunicationExpanded && (
                <Text fontSize="sm" color="gray.600" mb={0} align="left" ml={1} mt={1}>
                  Configure where to send job results
                </Text>
              )}
            </HStack>

            <Collapse in={isCommunicationExpanded} animateOpacity>
              <VStack spacing={4} align="stretch" p={3}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {/* Title */}
                  <FormControl>
                    <FormLabel fontSize="sm" mb={1} fontWeight="600">
                      Notification Title
                    </FormLabel>
                    <Input
                      size="sm"
                      borderRadius="md"
                      value={slackTitle}
                      onChange={(e) => setSlackTitle(e.target.value)}
                      placeholder="Enter notification title"
                      _focus={{
                        borderColor: "purple.700",
                        boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)",
                      }}
                      fontWeight="500"
                    />
                  </FormControl>

                  {/* Channel Selection */}
                  <FormControl>
                    <FormLabel fontSize="sm" mb={1} fontWeight="600">
                      Slack Channel
                    </FormLabel>
                    <Select
                      size="sm"
                      borderRadius="md"
                      value={selectedSlackChannel?.id || ""}
                      onChange={(e) => handleSlackChannelChange(e.target.value)}
                      placeholder="Choose a channel"
                    >
                      {slackChannels.map((channel) => (
                        <option key={channel.id} value={channel.id}>
                          {channel.name}
                        </option>
                      ))}
                    </Select>
                    {selectedSlackChannel && (
                      <Flex mt={2}>
                        <Badge colorScheme="blue" fontSize="xs">
                          {selectedSlackChannel.name}
                        </Badge>
                      </Flex>
                    )}
                  </FormControl>
                </SimpleGrid>

                {/* User Selection */}
                <FormControl>
                  <FormLabel fontSize="sm" mb={1} fontWeight="600">
                    Notify Users
                  </FormLabel>
                  <Popover placement="bottom-start" strategy="fixed">
                    <PopoverTrigger>
                      <Button
                        size="sm"
                        variant="outline"
                        isDisabled={!selectedSlackChannel || isLoadingUsers}
                        isLoading={isLoadingUsers}
                        loadingText="Loading users..."
                        bg={!selectedSlackChannel || isLoadingUsers ? "gray.100" : "white"}
                        textAlign="left"
                        fontWeight="normal"
                        width="100%"
                        justifyContent="flex-start"
                        _hover={!selectedSlackChannel || isLoadingUsers ? {} : { bg: "gray.50" }}
                      >
                        {isLoadingUsers
                          ? "Loading users..."
                          : selectedSlackUsers.length === 0
                            ? selectedSlackChannel
                              ? "Choose users"
                              : "Select a channel first"
                            : selectedSlackUsers.length === 1
                              ? selectedSlackUsers[0].display_name || selectedSlackUsers[0].name
                              : `${selectedSlackUsers.length} users selected`}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent width="300px" zIndex="popover">
                      <PopoverBody maxHeight="200px" overflowY="auto" p={3}>
                        <VStack spacing={2} align="stretch">
                          {isLoadingUsers ? (
                            <Text fontSize="sm" color="gray.500" textAlign="center">
                              Loading users...
                            </Text>
                          ) : channelUsers.length === 0 ? (
                            <Text fontSize="sm" color="gray.500" textAlign="center">
                              No users available
                            </Text>
                          ) : (
                            channelUsers.map((user) => (
                              <HStack key={user.id} spacing={3} w="100%" align="center">
                                <Checkbox
                                  isChecked={selectedSlackUsers.some((u) => u.id === user.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      if (!selectedSlackUsers.some((u) => u.id === user.id)) {
                                        setSelectedSlackUsers((prev) => [...prev, user]);
                                      }
                                    } else {
                                      setSelectedSlackUsers((prev) =>
                                        prev.filter((u) => u.id !== user.id)
                                      );
                                    }
                                  }}
                                  size="sm"
                                  colorScheme="purple"
                                />
                                <Text fontSize="sm" color="gray.700" ml={1} mt={1}>
                                  {user.display_name || user.name}
                                </Text>
                              </HStack>
                            ))
                          )}
                        </VStack>
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                  {selectedSlackUsers.length > 0 && (
                    <Flex mt={2} flexWrap="wrap" gap={2}>
                      {selectedSlackUsers.map((user) => (
                        <Badge
                          key={user.id}
                          colorScheme="purple"
                          fontSize="xs"
                          cursor="pointer"
                          onClick={() => removeSlackUser(user.id!)}
                          _hover={{ bg: "purple.200" }}
                          textTransform="none"
                        >
                          {user.display_name || user.name} ✕
                        </Badge>
                      ))}
                    </Flex>
                  )}
                </FormControl>
              </VStack>
            </Collapse>
          </FormControl>
        </VStack>

        <Divider mt={1} mb={1} />

        {/* Schedule Section */}
        <VStack spacing={3} align="stretch">
          <FormControl>
            <HStack
              spacing={2}
              cursor="pointer"
              onClick={toggleScheduleExpansion}
              _hover={{ bg: "gray.50" }}
              p={2}
              borderRadius="md"
              m={2}
              ml={1}
            >
              <Icon as={FiClock} boxSize={5} color="fg.default" />
              <Text fontSize="md" mb={0} cursor="pointer" fontWeight="700" color="fg.default">
                Schedule
              </Text>
              {!isScheduleExpanded && (
                <Text fontSize="sm" color="gray.600" mb={0} align="left" ml={1} mt={1}>
                  Configure when the job runs
                </Text>
              )}
            </HStack>

            <Collapse in={isScheduleExpanded} animateOpacity>
              <Box border="1px" borderColor="gray.200" borderRadius="md" m={2}>
                <CronBuilder
                  cronExpression={cronExpression}
                  setCronExpression={setCronExpression}
                  isActive={isActive}
                  setIsActive={setIsActive}
                  jobDetails={selectedJobDetails}
                />
              </Box>
            </Collapse>
          </FormControl>
        </VStack>

        <Divider mt={1} mb={1} />
      </Box>
    </Box>
  );
};

export default JobCreationComponent;
